/* eslint-disable @typescript-eslint/dot-notation */
import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { NbDialogService } from '@nebular/theme';
import { Actions, ofActionCompleted, Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { OktaRestriction } from 'src/app/core/services/okta-restrictions.service';
import { UtilityService } from 'src/app/core/services/utility.service';
import { RouterState } from 'src/app/features/auth/redux/router-state';
import { SourceItem } from 'src/app/models/drb-models/source-item';
import { ACTIONS, DRBTYPE, ENVIRONMENTS, MODE } from 'src/app/shared/constants/general-constants';
import { NgxGenericModalComponent } from 'src/app/shared/ui/ngx-generic-modal/ngx-generic-modal.component';
import { drbMetadata } from 'src/app/shared/utils/drb-metadata';
import { ENVIRONMENT } from 'src/environments/environment';
import { DeleteDrbComponent } from '../../delete-drb/delete-drb.component';
import { DrbState, LoadDrb, LoadDrbByVersionSuccess } from '../../redux';
import { EditSourceItemComponent } from './edit-source-item/edit-source-item.component';
import { ViewSourceItemComponent } from './view-source-item/view-source-item.component';
import { SharedModule } from '../../../../shared/shared.module';

@Component({
    selector: 'app-source-item',
    changeDetection: ChangeDetectionStrategy.Default,
    templateUrl: './source-item.component.html',
    styleUrls: ['./source-item.component.css'],
    providers: [DecimalPipe],
    imports: [SharedModule, RouterOutlet]
})
export class SourceItemComponent implements OnInit, OnDestroy {
  public drb$: Observable<any> = inject(Store).select(DrbState.drb);
  public file$: Observable<any[]> = inject(Store).select(DrbState.sourceFiles);
  public link$: Observable<any[]> = inject(Store).select(DrbState.sourceLinks);
  public video$: Observable<any[]> = inject(Store).select(DrbState.sourceVideos);
  public viewFile$: Observable<any[]> = inject(Store).select(DrbState.view_sourceFiles);
  public viewLink$: Observable<any[]> = inject(Store).select(DrbState.view_sourceLinks);
  public viewVideo$: Observable<any[]> = inject(Store).select(DrbState.view_sourceVideos);
  public username$: Observable<string> = inject(Store).select(RouterState.username);
  private unsubscribe: Subject<void> = new Subject();

  public tableData = [];
  public showActions = [];
  public tableName;
  public description;
  public metaData;
  public username;
  public type; //file-source, link-source, video-source
  public sourceType; // file, link, video
  public bundle_name: string;

  constructor(
    private router: Router,
    public route: ActivatedRoute,
    private store: Store,
    private action$: Actions,
    private dialogService: NbDialogService,
    public utility: UtilityService,
    private restrictionService: OktaRestriction
  ) {}

  public ngOnInit() {
    this.route.data.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.sourceType = data.type));
    this.bundle_name = this.router.url.split('/')[2];
    const createUrl = 'create/' + this.sourceType + 's';
    const viewUrl = 'view/' + this.sourceType + 's';
    this.type = this.sourceType + '-source';
    if (this.sourceType === DRBTYPE.FILE) {
      this.tableName = $localize`Files`;
    }
    if (this.sourceType === DRBTYPE.LINK) {
      this.tableName = $localize`Links`;
    }
    if (this.sourceType === DRBTYPE.VIDEO) {
      this.tableName = $localize`Videos`;
    }
    if (this.router.url.endsWith(createUrl)) {
      if (this.sourceType === DRBTYPE.FILE) {
        this.description = $localize`View, add and edit file data`;
      }
      if (this.sourceType === DRBTYPE.LINK) {
        this.description = $localize`View, add and edit link data`;
      }
      if (this.sourceType === DRBTYPE.VIDEO) {
        this.description = $localize`View, add and edit video data`;
      }
      this.loadTableData(ACTIONS.EDIT);
      this.loadMetaData();

      this.username$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.username = data));
      this.checkOwnerOrContributor();
    } else if (this.router.url.endsWith(viewUrl)) {
      if (this.sourceType === DRBTYPE.FILE) {
        this.description = $localize`View file data`;
      }
      if (this.sourceType === DRBTYPE.LINK) {
        this.description = $localize`View link data`;
      }
      if (this.sourceType === DRBTYPE.VIDEO) {
        this.description = $localize`View video data`;
      }
      this.loadTableData(ACTIONS.VIEW);
      this.action$.pipe(ofActionCompleted(LoadDrbByVersionSuccess), takeUntil(this.unsubscribe)).subscribe((data) => {
        if (data) {
          this.loadTableData(ACTIONS.VIEW);
        }
      });
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public loadTableData(mode) {
    if (mode === MODE.VIEW) {
      if (this.sourceType === DRBTYPE.FILE) {
        this.viewFile$.pipe(takeUntil(this.unsubscribe)).subscribe((loadedData) => (this.tableData = loadedData));
      }
      if (this.sourceType === DRBTYPE.LINK) {
        this.viewLink$.pipe(takeUntil(this.unsubscribe)).subscribe((loadedData) => (this.tableData = loadedData));
      }
      if (this.sourceType === DRBTYPE.VIDEO) {
        this.viewVideo$.pipe(takeUntil(this.unsubscribe)).subscribe((loadedData) => (this.tableData = loadedData));
      }
    } else {
      if (this.sourceType === DRBTYPE.FILE) {
        this.file$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.tableData = data));
      }
      if (this.sourceType === DRBTYPE.LINK) {
        this.link$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.tableData = data));
      }
      if (this.sourceType === DRBTYPE.VIDEO) {
        this.video$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.tableData = data));
      }
    }
  }

  public loadMetaData() {
    this.drb$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      this.metaData = drbMetadata(data);
    });
  }

  public clickedButton($event) {
    if ($event.action === ACTIONS.REFRESH) {
      this.dialogService
        .open(NgxGenericModalComponent, {
          context: {
            title: $localize`Confirm Refresh`,
            message: $localize`Are you sure that you want to refresh draft version?`
          }
        })
        .onClose.subscribe((data) => {
          if (data) {
            this.tableData = undefined;
            this.store.dispatch(new LoadDrb(this.bundle_name));
          }
        });
    }
    if ($event.action === ACTIONS.CREATE) {
      this.dialogService.open(EditSourceItemComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          sourceItem: new SourceItem(),
          type: this.sourceType,
          bundle_name: this.bundle_name
        }
      });
    }
    if ($event.action === ACTIONS.VIEW) {
      const sourceItem: SourceItem = $event.data;
      this.dialogService.open(ViewSourceItemComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          sourceItem,
          type: this.sourceType
        }
      });
    }
    if ($event.action === ACTIONS.EDIT) {
      const sourceItem: SourceItem = $event.data;
      this.dialogService.open(EditSourceItemComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          sourceItem: JSON.parse(JSON.stringify(sourceItem)),
          type: this.sourceType,
          bundle_name: this.bundle_name
        }
      });
    }
    if ($event.action === ACTIONS.DELETE) {
      const sourceItem: SourceItem = $event.data;
      this.dialogService.open(DeleteDrbComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          item: JSON.parse(JSON.stringify(sourceItem)),
          type: this.sourceType,
          bundle_name: this.bundle_name
        }
      });
    }
  }

  public checkOwnerOrContributor() {
    if (this.restrictionService.manager_permission) {
      if (this.username === this.metaData.owner || this.metaData.contributors.includes(this.username)) {
        this.showActions = [ACTIONS.CREATE, ACTIONS.EDIT];
        if (ENVIRONMENT.name === ENVIRONMENTS.STAGE) {
          this.showActions.push(ACTIONS.DELETE);
        }
      }
    }
  }
}
